import { nanoid } from '@reduxjs/toolkit'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
// import DailyInspiration, {
//   marcusAureliusMeditations7_3,
// } from '../../Components/DailyInspiration/DailyInspiration'
import PomodoroModal from '../../Components/PomodoroModal/PomodoroModal'
import PriorityPopover from '../../Components/PriorityPopover/PriorityPopover'
import { SkeletonSidebarCards, SkeletonTaskDetailsMain } from '../../Components/Skeleton/Skeleton'
import SchedulePopover from '../../Components/SchedulePopover/SchedulePopover'
import { TagInput } from '../../Components/TagInput'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import type { Priority, Status, UpdateTaskItemRequest } from '../../Models/Task'
import {
  clearCommentsError,
  createComment,
  deleteComment,
  fetchComments,
  selectCommentsByTaskId,
  selectCommentsError,
  selectCommentsStatus,
} from '../../redux/comments'
import { selectTaskById, selectTasksStatus, todayKey, updateTask } from '../../redux/tasks'
import {
  fetchDocuments,
  fetchDocument,
  fetchFiles,
  fetchFolders,
  selectDocuments,
  updateDocument,
  updateFolder,
  selectLinkedDocuments,
  selectLinkedFiles,
  selectLinkedFolders,
  selectFolders,
} from '../../redux/workspace'
import styles from './TaskDetails.module.css'

const PRIORITY_LABEL: Record<Priority, string> = {
  p1: 'P1 Priority',
  p2: 'P2 Priority',
  p3: 'P3 Priority',
  p4: 'P4 Priority',
}

const PRIORITY_CLASS: Record<Priority, string> = {
  p1: styles.pri_p1,
  p2: styles.pri_p2,
  p3: styles.pri_p3,
  p4: styles.pri_p4,
}

const STATUS_LABEL: Record<Status, string> = {
  todo: 'To Do',
  in_progress: 'In Progress',
  review: 'In Review',
  done: 'Done',
}

const STATUS_ORDER: Status[] = ['todo', 'in_progress', 'review', 'done']

const parseDueDate = (ymd: string) => {
  const [y, m, d] = ymd.split('-').map(Number)
  return new Date(y, m - 1, d)
}

const isDueToday = (ymd: string | undefined) => {
  if (!ymd) return false
  const now = new Date()
  const due = parseDueDate(ymd)
  return (
    due.getFullYear() === now.getFullYear() &&
    due.getMonth() === now.getMonth() &&
    due.getDate() === now.getDate()
  )
}

const formatDue = (ymd: string | undefined) => {
  if (!ymd) return 'Unscheduled'
  if (isDueToday(ymd)) return 'Due Today'
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(parseDueDate(ymd))
}

/** Label for deadline row; empty state invites picking a date. */
const formatDeadlineLabel = (ymd: string | undefined) => {
  if (!ymd) return 'No deadline'
  if (isDueToday(ymd)) return 'Due today'
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  }).format(parseDueDate(ymd))
}

const isDeadlinePastOrToday = (ymd: string | undefined) =>
  !!ymd && ymd <= todayKey()

const formatRelative = (iso?: string) => {
  if (!iso) return ''
  const diff = Date.now() - new Date(iso).getTime()
  const mins = Math.round(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins} minute${mins === 1 ? '' : 's'} ago`
  const hrs = Math.round(mins / 60)
  if (hrs < 24) return `${hrs} hour${hrs === 1 ? '' : 's'} ago`
  const days = Math.round(hrs / 24)
  if (days === 1) return 'Yesterday'
  if (days < 7) return `${days} days ago`
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
  }).format(new Date(iso))
}

const ClockIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
    <path d="M12 7v5l3 2" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const BackIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M15 6l-6 6 6 6"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const CalendarIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="3.5" y="5" width="17" height="15" rx="2.5" stroke="currentColor" strokeWidth="1.75" />
    <path d="M3.5 9.5h17" stroke="currentColor" strokeWidth="1.75" />
    <path d="M8 3.5v3M16 3.5v3" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
  </svg>
)

const RocketIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M14 4s6 0 6 6c0 0-4 0-6-2M14 4l-6 6H4l4 4 4 4v-4l6-6M14 4c-2 2-2 6-2 6l4 4s4 0 6-2"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinejoin="round"
      strokeLinecap="round"
    />
  </svg>
)

const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M5 12l5 5L20 7"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
)

const PencilIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 20h4l10-10-4-4L4 16v4z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TrashIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6h14zM10 11v6M14 11v6"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ChatIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M21 12a8 8 0 01-11.6 7.1L4 21l1.9-5.4A8 8 0 1121 12z"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const EmojiIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M8 14s1.5 2 4 2 4-2 4-2"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
    />
    <circle cx="9" cy="10" r="1" fill="currentColor" />
    <circle cx="15" cy="10" r="1" fill="currentColor" />
  </svg>
)

const PaperclipIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.67 3.67 0 015.19 5.19l-9.2 9.19a1.83 1.83 0 11-2.59-2.59l8.48-8.48"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const AtIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.75" />
    <path
      d="M16 8v5a3 3 0 006 0v-1a10 10 0 10-3.92 7.94"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const TaskDetails = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const task = useAppSelector(selectTaskById(id))
  const tasksStatus = useAppSelector(selectTasksStatus)
  const comments = useAppSelector(selectCommentsByTaskId(id))
  const commentsStatus = useAppSelector(selectCommentsStatus)
  const commentsError = useAppSelector(selectCommentsError)
  const linkedDocuments = useAppSelector(selectLinkedDocuments(id ?? ''))
  const linkedFiles = useAppSelector(selectLinkedFiles(id ?? ''))
  const linkedFolders = useAppSelector(selectLinkedFolders(id ?? ''))
  const folders = useAppSelector(selectFolders)
  const allDocuments = useAppSelector(selectDocuments)
  const [linkingTarget, setLinkingTarget] = useState<'document' | 'folder' | null>(null)

  // Task list is loaded by `TasksBootstrap` in `App` and by dashboard; do not `fetchTasks()`
  // here — it raced PATCH (e.g. tags) when a slow GetTaskItems returned after an update.

  useEffect(() => {
    if (id) void dispatch(fetchComments(id))
  }, [dispatch, id])

  useEffect(() => {
    if (id) {
      void dispatch(fetchDocuments({ taskId: id }))
      void dispatch(fetchFiles({ taskId: id }))
      void dispatch(fetchFolders())
    }
  }, [dispatch, id])

  const linkDocumentToTask = async (documentId: string) => {
    if (!id) return
    const doc = await dispatch(fetchDocument(documentId)).unwrap()
    if (doc.linkedTaskIds.includes(id)) return
    await dispatch(
      updateDocument({
        id: documentId,
        patch: { linkedTaskIds: [...doc.linkedTaskIds, id] },
      })
    )
    setLinkingTarget(null)
    void dispatch(fetchDocuments({ taskId: id }))
  }

  const unlinkDocumentFromTask = async (documentId: string) => {
    if (!id) return
    const doc = await dispatch(fetchDocument(documentId)).unwrap()
    await dispatch(
      updateDocument({
        id: documentId,
        patch: { linkedTaskIds: doc.linkedTaskIds.filter((taskId) => taskId !== id) },
      })
    )
    void dispatch(fetchDocuments({ taskId: id }))
  }

  const linkFolderToTask = async (folderId: string) => {
    if (!id) return
    const folder = folders.find((f) => f.id === folderId)
    if (!folder || folder.linkedTaskIds.includes(id)) return
    await dispatch(
      updateFolder({
        id: folderId,
        patch: { linkedTaskIds: [...folder.linkedTaskIds, id] },
      })
    )
    setLinkingTarget(null)
    void dispatch(fetchFolders())
  }

  const unlinkFolderFromTask = async (folderId: string) => {
    if (!id) return
    const folder = folders.find((f) => f.id === folderId)
    if (!folder) return
    await dispatch(
      updateFolder({
        id: folderId,
        patch: { linkedTaskIds: folder.linkedTaskIds.filter((taskId) => taskId !== id) },
      })
    )
    void dispatch(fetchFolders())
  }

  const [adding, setAdding] = useState(false)
  const [newStep, setNewStep] = useState('')

  const [editingTitle, setEditingTitle] = useState(false)
  const [titleDraft, setTitleDraft] = useState('')
  const [editingDescription, setEditingDescription] = useState(false)
  const [descriptionDraft, setDescriptionDraft] = useState('')
  const [scheduleOpen, setScheduleOpen] = useState(false)
  const [deadlineOpen, setDeadlineOpen] = useState(false)
  const [priorityOpen, setPriorityOpen] = useState(false)
  const [commentBody, setCommentBody] = useState('')
  const [postingComment, setPostingComment] = useState(false)
  const [pomodoroOpen, setPomodoroOpen] = useState(false)
  const descTextareaRef = useRef<HTMLTextAreaElement | null>(null)

  useEffect(() => {
    if (editingDescription && descTextareaRef.current) {
      const el = descTextareaRef.current
      el.focus()
      el.setSelectionRange(el.value.length, el.value.length)
      el.style.height = 'auto'
      el.style.height = `${el.scrollHeight}px`
    }
  }, [editingDescription])

  const subtasks = useMemo(() => task?.subtasks ?? [], [task?.subtasks])
  const doneCount = useMemo(
    () => subtasks.filter((s) => s.completed).length,
    [subtasks]
  )

  const cycleStatus = () => {
    if (!task) return
    const current = task.status ?? (task.completedAt ? 'done' : 'todo')
    const next = STATUS_ORDER[(STATUS_ORDER.indexOf(current) + 1) % STATUS_ORDER.length]
    const changes: UpdateTaskItemRequest = { status: next }
    if (next === 'done' && !task.completedAt) {
      changes.completedAt = new Date().toISOString()
    } else if (next !== 'done' && task.completedAt) {
      changes.completedAt = null
    }
    void dispatch(updateTask({ id: task.id, changes }))
  }

  const submitStep = () => {
    if (!task) return
    const trimmed = newStep.trim()
    if (!trimmed) {
      setAdding(false)
      return
    }
    const subtasks = [...(task.subtasks ?? []), { id: nanoid(), title: trimmed, completed: false }]
    void dispatch(updateTask({ id: task.id, changes: { subtasks } }))
    setNewStep('')
    setAdding(false)
  }

  const startEditingTitle = () => {
    if (!task) return
    setTitleDraft(task.title)
    setEditingTitle(true)
  }

  const commitTitle = () => {
    if (!task) return
    const trimmed = titleDraft.trim()
    if (trimmed && trimmed !== task.title) {
      void dispatch(updateTask({ id: task.id, changes: { title: trimmed } }))
    }
    setEditingTitle(false)
  }

  const cancelTitle = () => {
    setEditingTitle(false)
    setTitleDraft('')
  }

  const startEditingDescription = () => {
    if (!task) return
    setDescriptionDraft(task.description ?? '')
    setEditingDescription(true)
  }

  const commitDescription = () => {
    if (!task) return
    const trimmed = descriptionDraft.trim()
    const next = trimmed.length > 0 ? trimmed : null
    if ((task.description ?? '') !== trimmed) {
      void dispatch(updateTask({ id: task.id, changes: { description: next } }))
    }
    setEditingDescription(false)
  }

  const cancelDescription = () => {
    setEditingDescription(false)
    setDescriptionDraft('')
  }

  const reschedule = (dateKey: string) => {
    if (!task) return
    void dispatch(updateTask({ id: task.id, changes: { scheduledDate: dateKey } }))
    setScheduleOpen(false)
  }

  const unschedule = () => {
    if (!task) return
    void dispatch(updateTask({ id: task.id, changes: { scheduledDate: null } }))
    setScheduleOpen(false)
  }

  const pickDeadline = (dateKey: string) => {
    if (!task) return
    void dispatch(updateTask({ id: task.id, changes: { deadlineDate: dateKey } }))
    setDeadlineOpen(false)
  }

  const clearDeadline = () => {
    if (!task) return
    void dispatch(updateTask({ id: task.id, changes: { deadlineDate: null } }))
    setDeadlineOpen(false)
  }

  const pickPriority = (priority: Priority | undefined) => {
    if (!task) return
    void dispatch(updateTask({ id: task.id, changes: { priority: priority ?? null } }))
    setPriorityOpen(false)
  }

  const submitComment = async () => {
    if (!task) return
    const trimmed = commentBody.trim()
    if (!trimmed) return
    setPostingComment(true)
    try {
      await dispatch(createComment({ taskId: task.id, body: trimmed })).unwrap()
      setCommentBody('')
    } catch {
      /* error in slice */
    } finally {
      setPostingComment(false)
    }
  }

  const removeComment = async (commentId: string) => {
    if (!task) return
    try {
      await dispatch(deleteComment({ taskId: task.id, id: commentId })).unwrap()
    } catch {
      /* error in slice */
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.crumbs}>
          <button
            type="button"
            className={styles.crumbBack}
            onClick={() => navigate(-1)}
            aria-label="Back"
          >
            <BackIcon />
          </button>
          <button type="button" className={styles.crumbLink} onClick={() => navigate('/')}>
            Dashboard
          </button>
          <span className={styles.crumbSep}>/</span>
          <span className={styles.crumbCurrent}>{task?.project ?? 'Task'}</span>
        </div>
      </div>

      {tasksStatus === 'loading' && !task ? (
        <div className={styles.grid}>
          <div className={styles.main}>
            <SkeletonTaskDetailsMain />
          </div>
          <aside className={styles.sidebar}>
            <SkeletonSidebarCards />
          </aside>
        </div>
      ) : !task ? (
        <div className={styles.notFound}>
          <p>We couldn't find that task.</p>
        </div>
      ) : (
        <div className={styles.grid}>
          <div className={styles.main}>
            <div className={styles.pillRow}>
              <div className={styles.popoverAnchor}>
                <button
                  type="button"
                  className={`${styles.priorityPill} ${
                    task.priority ? PRIORITY_CLASS[task.priority] : styles.priorityPillEmpty
                  }`}
                  onClick={() => setPriorityOpen((v) => !v)}
                  aria-expanded={priorityOpen}
                  aria-label={task.priority ? 'Change priority' : 'Set priority'}
                >
                  {task.priority ? PRIORITY_LABEL[task.priority] : '+ Priority'}
                </button>
                {priorityOpen ? (
                  <PriorityPopover
                    current={task.priority}
                    onPick={pickPriority}
                    onClose={() => setPriorityOpen(false)}
                    align="left"
                  />
                ) : null}
              </div>
              <button
                type="button"
                className={styles.statusPill}
                onClick={cycleStatus}
                aria-label="Change status"
              >
                <span
                  className={`${styles.statusDot} ${
                    (task.status ?? 'todo') === 'done' ? styles.statusDotFilled : ''
                  }`}
                />
                {STATUS_LABEL[task.status ?? (task.completedAt ? 'done' : 'todo')]}
              </button>
              <button
                type="button"
                className={styles.pomodoroPillBtn}
                onClick={() => setPomodoroOpen(true)}
                aria-label={`Start Pomodoro for ${task.title}`}
              >
                <ClockIcon />
              </button>
            </div>

            {editingTitle ? (
              <input
                type="text"
                className={styles.titleInput}
                value={titleDraft}
                autoFocus
                onChange={(e) => setTitleDraft(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault()
                    commitTitle()
                  } else if (e.key === 'Escape') {
                    e.preventDefault()
                    cancelTitle()
                  }
                }}
                aria-label="Edit task title"
              />
            ) : (
              <h1
                className={`${styles.title} ${styles.titleEditable}`}
                onClick={startEditingTitle}
                tabIndex={0}
                role="button"
                aria-label="Edit task title"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    startEditingTitle()
                  }
                }}
              >
                {task.title}
              </h1>
            )}

            <div>
              <p className={styles.sectionLabel}>Description</p>
              {editingDescription ? (
                <div className={styles.descCard}>
                  <textarea
                    ref={descTextareaRef}
                    className={styles.descTextarea}
                    value={descriptionDraft}
                    placeholder="Add a description..."
                    onChange={(e) => {
                      setDescriptionDraft(e.target.value)
                      const el = e.currentTarget
                      el.style.height = 'auto'
                      el.style.height = `${el.scrollHeight}px`
                    }}
                    onBlur={commitDescription}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault()
                        commitDescription()
                      } else if (e.key === 'Escape') {
                        e.preventDefault()
                        cancelDescription()
                      }
                    }}
                    aria-label="Edit description"
                  />
                  <div className={styles.descHint}>
                    <kbd>Esc</kbd> to cancel · <kbd>⌘/Ctrl</kbd> + <kbd>Enter</kbd> to save
                  </div>
                </div>
              ) : (
                <div
                  className={`${styles.descCard} ${styles.descCardEditable}`}
                  onClick={startEditingDescription}
                  role="button"
                  tabIndex={0}
                  aria-label="Edit description"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      startEditingDescription()
                    }
                  }}
                >
                  {task.description ? (
                    <p className={styles.descBody}>{task.description}</p>
                  ) : (
                    <p className={`${styles.descBody} ${styles.descEmpty}`}>
                      No description yet. Click to add one.
                    </p>
                  )}
                  {task.metaLine ? (
                    <span className={styles.attachment}>
                      <span aria-hidden>📎</span>
                      {task.metaLine}
                    </span>
                  ) : null}
                </div>
              )}
            </div>

            <div>
              <div className={styles.subtasksHead}>
                <p className={styles.sectionLabel}>Subtasks</p>
                {subtasks.length > 0 ? (
                  <span className={styles.subtasksCount}>
                    {doneCount} / {subtasks.length} Completed
                  </span>
                ) : null}
              </div>
              <div className={styles.subtasksList}>
                {subtasks.map((s) => (
                  <div
                    key={s.id}
                    className={`${styles.subItem} ${s.completed ? styles.subDone : ''}`}
                  >
                    <button
                      type="button"
                      className={`${styles.subCheck} ${
                        s.completed ? styles.subCheckDone : ''
                      }`}
                      aria-pressed={s.completed}
                      aria-label={
                        s.completed ? `Uncheck ${s.title}` : `Check ${s.title}`
                      }
                      onClick={() => {
                        const subtasks = (task.subtasks ?? []).map((x) =>
                          x.id === s.id ? { ...x, completed: !x.completed } : x
                        )
                        void dispatch(updateTask({ id: task.id, changes: { subtasks } }))
                      }}
                    >
                      {s.completed ? <CheckIcon /> : null}
                    </button>
                    <span className={styles.subTitle}>{s.title}</span>
                  </div>
                ))}
                {adding ? (
                  <div className={styles.addStepForm}>
                    <input
                      autoFocus
                      className={styles.addStepInput}
                      placeholder="Step description"
                      value={newStep}
                      onChange={(e) => setNewStep(e.target.value)}
                      onBlur={submitStep}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') submitStep()
                        if (e.key === 'Escape') {
                          setAdding(false)
                          setNewStep('')
                        }
                      }}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    className={styles.addStep}
                    onClick={() => setAdding(true)}
                  >
                    <PlusIcon /> Add a step...
                  </button>
                )}
              </div>
            </div>

            <div className={styles.discussion}>
              <p className={styles.sectionLabel}>
                <ChatIcon /> Notes
              </p>
              {commentsError ? (
                <div className={styles.commentsError} role="alert">
                  <span>{commentsError}</span>
                  <button
                    type="button"
                    className={styles.commentsErrorDismiss}
                    onClick={() => dispatch(clearCommentsError())}
                  >
                    Dismiss
                  </button>
                </div>
              ) : null}

              {commentsStatus === 'loading' && comments.length === 0 ? (
                <p className={styles.commentsLoading}>Loading comments…</p>
              ) : comments.length > 0 ? (
                <ul className={styles.commentsList}>
                  {comments.map((c) => (
                    <li key={c.id} className={styles.commentItem}>
                      <div className={styles.commentAvatar} aria-hidden>
                        Y
                      </div>
                      <div className={styles.commentContent}>
                        <div className={styles.commentHeader}>
                          <span className={styles.commentAuthor}>You</span>
                          <span className={styles.commentTime}>
                            {formatRelative(c.createdAt)}
                          </span>
                          <button
                            type="button"
                            className={styles.commentDelete}
                            aria-label="Delete comment"
                            onClick={() => void removeComment(c.id)}
                          >
                            <TrashIcon />
                          </button>
                        </div>
                        <p className={styles.commentBody}>{c.body}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : commentsStatus === 'failed' ? null : (
                <p className={styles.commentsEmpty}>
                  No comments yet — share the first thought.
                </p>
              )}

              <div className={styles.composer}>
                <div className={styles.composerAvatar} aria-hidden>
                  Y
                </div>
                <div className={styles.composerMain}>
                  <textarea
                    className={styles.composerTextarea}
                    placeholder="Share a thought with the team…"
                    rows={3}
                    value={commentBody}
                    onChange={(e) => setCommentBody(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        e.preventDefault()
                        void submitComment()
                      }
                    }}
                    aria-label="Comment text"
                  />
                  <div className={styles.composerFooter}>
                    <div className={styles.composerTools}>
                      <button
                        type="button"
                        className={styles.composerToolBtn}
                        aria-label="Add emoji"
                        title="Emoji (coming soon)"
                        disabled
                      >
                        <EmojiIcon />
                      </button>
                      <button
                        type="button"
                        className={styles.composerToolBtn}
                        aria-label="Attach file"
                        title="Attach (coming soon)"
                        disabled
                      >
                        <PaperclipIcon />
                      </button>
                      <button
                        type="button"
                        className={styles.composerToolBtn}
                        aria-label="Mention someone"
                        title="Mention (coming soon)"
                        disabled
                      >
                        <AtIcon />
                      </button>
                    </div>
                    <button
                      type="button"
                      className={styles.postCommentBtn}
                      disabled={postingComment || !commentBody.trim()}
                      onClick={() => void submitComment()}
                    >
                      {postingComment ? 'Posting…' : 'Post Comment'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <aside className={styles.sidebar}>
            <div className={styles.sideCard}>
              <div className={styles.sideRow}>
                <span className={styles.sideLabel}>Scheduled</span>
                <div className={styles.popoverAnchor}>
                  <button
                    type="button"
                    className={`${styles.sideValueBtn} ${
                      isDueToday(task.scheduledDate) ? styles.dueDanger : ''
                    }`}
                    onClick={() => {
                      setDeadlineOpen(false)
                      setScheduleOpen((v) => !v)
                    }}
                    aria-expanded={scheduleOpen}
                    aria-label="Change scheduled date"
                  >
                    <CalendarIcon />
                    {formatDue(task.scheduledDate)}
                  </button>
                  {scheduleOpen ? (
                    <SchedulePopover
                      currentScheduledDate={task.scheduledDate}
                      onPick={reschedule}
                      onUnschedule={unschedule}
                      onClose={() => setScheduleOpen(false)}
                      align="left"
                      unscheduleLabel="Remove scheduled date"
                    />
                  ) : null}
                </div>
              </div>

              <div className={styles.sideRow}>
                <span className={styles.sideLabel}>Deadline</span>
                <div className={styles.popoverAnchor}>
                  <button
                    type="button"
                    className={`${styles.sideValueBtn} ${
                      isDeadlinePastOrToday(task.deadlineDate) ? styles.dueDanger : ''
                    }`}
                    onClick={() => {
                      setScheduleOpen(false)
                      setDeadlineOpen((v) => !v)
                    }}
                    aria-expanded={deadlineOpen}
                    aria-label="Change deadline"
                  >
                    <CalendarIcon />
                    {formatDeadlineLabel(task.deadlineDate)}
                  </button>
                  {deadlineOpen ? (
                    <SchedulePopover
                      currentScheduledDate={task.deadlineDate}
                      onPick={pickDeadline}
                      onUnschedule={clearDeadline}
                      onClose={() => setDeadlineOpen(false)}
                      align="left"
                      unscheduleLabel="Remove deadline"
                    />
                  ) : null}
                </div>
              </div>

              {task.project ? (
                <div className={styles.sideRow}>
                  <span className={styles.sideLabel}>Project</span>
                  <span className={styles.sideValue}>
                    <RocketIcon />
                    {task.project}
                  </span>
                </div>
              ) : null}

              <div className={styles.sideRow}>
                <span className={styles.sideLabel}>Tags</span>
                <TagInput
                  id="task-detail-tags"
                  tags={task.labels ?? []}
                  onChange={(next) => {
                    void dispatch(updateTask({ id: task.id, changes: { labels: next } }))
                  }}
                  aria-label="Task tags"
                />
              </div>

              {task.owner ? (
                <div className={styles.sideRow}>
                  <span className={styles.sideLabel}>Owner</span>
                  <span className={styles.sideValue}>
                    <span className={styles.ownerAvatar} aria-hidden>
                      {task.owner.initials}
                    </span>
                    {task.owner.name}
                  </span>
                </div>
              ) : null}
            </div>

            {/* DailyInspiration sidebar — hidden for now; re-enable import + component above */}

            <div className={styles.sideCard}>
              <p className={styles.sectionLabel}>Activity</p>
              <div className={styles.activityList}>
                {task.completedAt ? (
                  <div className={styles.activityItem}>
                    <span className={styles.activityText}>
                      Task moved to <em>Done</em>
                    </span>
                    <span className={styles.activityTime}>
                      {formatRelative(task.completedAt)}
                    </span>
                  </div>
                ) : null}
                <div className={styles.activityItem}>
                  <span className={styles.activityText}>
                    Task <em>created</em>
                  </span>
                  <span className={styles.activityTime}>
                    {formatRelative(task.createdAt)}
                  </span>
                </div>
              </div>
            </div>

            {(linkedDocuments.length > 0 || linkedFiles.length > 0 || linkedFolders.length > 0 || id) && (
              <div className={styles.sideCard}>
                <p className={styles.sectionLabel}>Linked Documents</p>
                <div className={styles.linkActions}>
                  <button
                    type="button"
                    className={styles.linkActionBtn}
                    onClick={() => setLinkingTarget(linkingTarget === 'document' ? null : 'document')}
                  >
                    {linkingTarget === 'document' ? 'Cancel' : 'Link document'}
                  </button>
                  <button
                    type="button"
                    className={styles.linkActionBtn}
                    onClick={() => setLinkingTarget(linkingTarget === 'folder' ? null : 'folder')}
                  >
                    {linkingTarget === 'folder' ? 'Cancel' : 'Link folder'}
                  </button>
                </div>
                {linkingTarget === 'document' && (
                  <ul className={styles.linkedList}>
                    {allDocuments
                      .filter((doc) => !(id ? doc.linkedTaskIds.includes(id) : false))
                      .map((doc) => (
                        <li key={`pick-doc-${doc.id}`}>
                          <button
                            type="button"
                            className={styles.linkedItem}
                            onClick={() => void linkDocumentToTask(doc.id)}
                          >
                            + {doc.title || 'Untitled'}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
                {linkingTarget === 'folder' && (
                  <ul className={styles.linkedList}>
                    {folders
                      .filter((folder) => !(id ? folder.linkedTaskIds.includes(id) : false))
                      .map((folder) => (
                        <li key={`pick-folder-${folder.id}`}>
                          <button
                            type="button"
                            className={styles.linkedItem}
                            onClick={() => void linkFolderToTask(folder.id)}
                          >
                            + {folder.name}
                          </button>
                        </li>
                      ))}
                  </ul>
                )}
                {linkedDocuments.length > 0 ? (
                  <ul className={styles.linkedList}>
                    {linkedDocuments.map((doc) => (
                      <li key={doc.id}>
                        <div className={styles.linkedRow}>
                          <button
                            type="button"
                            className={styles.linkedItem}
                            onClick={() => navigate(`/workspace/${doc.id}`)}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8l-6-6z"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinejoin="round"
                              />
                              <path d="M14 2v6h6M8 13h8M8 17h5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
                            </svg>
                            {doc.title || 'Untitled'}
                          </button>
                          <button
                            type="button"
                            className={styles.unlinkBtn}
                            onClick={() => void unlinkDocumentFromTask(doc.id)}
                            aria-label={`Unlink ${doc.title || 'Untitled'}`}
                          >
                            ×
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                ) : null}
                {linkedFolders.length > 0 ? (
                  <>
                    <p className={styles.linkedSubLabel}>Linked Folders</p>
                    <ul className={styles.linkedList}>
                      {linkedFolders.map((folder) => (
                        <li key={folder.id}>
                          <div className={styles.linkedRow}>
                            <button
                              type="button"
                              className={styles.linkedItem}
                              onClick={() => navigate('/workspace')}
                            >
                              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                                <path
                                  d="M3 7a2 2 0 012-2h4l2 2h8a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V7z"
                                  stroke="currentColor"
                                  strokeWidth="1.75"
                                  strokeLinejoin="round"
                                />
                              </svg>
                              {folder.name}
                            </button>
                            <button
                              type="button"
                              className={styles.unlinkBtn}
                              onClick={() => void unlinkFolderFromTask(folder.id)}
                              aria-label={`Unlink ${folder.name}`}
                            >
                              ×
                            </button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
                {linkedFiles.length > 0 ? (
                  <>
                    <p className={styles.linkedSubLabel}>Attached Files</p>
                    <ul className={styles.linkedList}>
                      {linkedFiles.map((file) => (
                        <li key={file.id}>
                          <a
                            href={file.blobUrl}
                            target="_blank"
                            rel="noreferrer"
                            className={styles.linkedItem}
                          >
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
                              <path
                                d="M21.44 11.05l-9.19 9.19a5.5 5.5 0 01-7.78-7.78l9.19-9.19a3.67 3.67 0 015.19 5.19l-9.2 9.19a1.83 1.83 0 11-2.59-2.59l8.48-8.48"
                                stroke="currentColor"
                                strokeWidth="1.75"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                            {file.fileName}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </>
                ) : null}
              </div>
            )}
          </aside>
        </div>
      )}

      <button
        type="button"
        className={styles.fab}
        aria-label={task?.completedAt ? 'Mark incomplete' : 'Mark complete'}
        onClick={() =>
          task &&
          void dispatch(
            updateTask({
              id: task.id,
              changes: task.completedAt
                ? { completedAt: null, status: 'todo' }
                : { completedAt: new Date().toISOString(), status: 'done' },
            })
          )
        }
      >
        <PencilIcon />
      </button>

      <PomodoroModal
        key={task?.id ?? 'closed'}
        isOpen={pomodoroOpen && !!task}
        taskTitle={task?.title ?? ''}
        onClose={() => setPomodoroOpen(false)}
      />
    </div>
  )
}

export default TaskDetails
